/**
 * Home-page favourites, stored per user in localStorage.
 *
 * Deliberately browser-local: these are a personal shortcut preference, not shared
 * business data, so there is no server round-trip and nothing to deploy. The
 * trade-off is that favourites do not follow a user to another browser or device.
 * Moving them server-side means a table, schema fields, resolvers and a deploy.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { User } from '../types/user';
import { MAX_FAVORITES } from '../utils/favoritePages';

const STORAGE_PREFIX = 'creyente:favorites:v1';

/** Keyed by email so the same person keeps favourites across id changes. */
const storageKeyFor = (user: User | null | undefined): string | null => {
  if (!user) return null;
  const identity = user.email || user.id;
  return identity ? `${STORAGE_PREFIX}:${identity.toLowerCase()}` : null;
};

const readStored = (key: string | null): string[] => {
  if (!key) return [];
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((entry): entry is string => typeof entry === 'string');
  } catch {
    // Private browsing, disabled storage, or hand-edited garbage — start empty
    // rather than breaking the page.
    return [];
  }
};

export interface UseFavoritesResult {
  favorites: string[];
  isFavorite: (path: string) => boolean;
  toggleFavorite: (path: string) => void;
  removeFavorite: (path: string) => void;
  /** True once MAX_FAVORITES is reached; pinning more is refused. */
  isFull: boolean;
}

export const useFavorites = (user: User | null | undefined): UseFavoritesResult => {
  const storageKey = useMemo(() => storageKeyFor(user), [user]);
  const [favorites, setFavorites] = useState<string[]>(() => readStored(storageKey));

  // Re-read when the signed-in user changes; each identity has its own key.
  useEffect(() => {
    setFavorites(readStored(storageKey));
  }, [storageKey]);

  // Keep other tabs of the same account in step.
  useEffect(() => {
    if (!storageKey) return;
    const onStorage = (event: StorageEvent) => {
      if (event.key === storageKey) setFavorites(readStored(storageKey));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [storageKey]);

  /**
   * Applies `update` to the current list and persists the result. Always derives
   * from the latest state so rapid clicks cannot write a stale list back.
   */
  const apply = useCallback((update: (current: string[]) => string[]) => {
    setFavorites(current => {
      const next = update(current);
      if (next === current) return current;
      if (storageKey) {
        try {
          window.localStorage.setItem(storageKey, JSON.stringify(next));
        } catch {
          // Storage full or unavailable. The in-memory list still works for this
          // session; only persistence is lost.
        }
      }
      return next;
    });
  }, [storageKey]);

  const toggleFavorite = useCallback((path: string) => {
    apply(current => {
      if (current.includes(path)) return current.filter(entry => entry !== path);
      if (current.length >= MAX_FAVORITES) return current;
      return [...current, path];
    });
  }, [apply]);

  const removeFavorite = useCallback((path: string) => {
    apply(current => (current.includes(path) ? current.filter(entry => entry !== path) : current));
  }, [apply]);

  const isFavorite = useCallback((path: string) => favorites.includes(path), [favorites]);

  return {
    favorites,
    isFavorite,
    toggleFavorite,
    removeFavorite,
    isFull: favorites.length >= MAX_FAVORITES,
  };
};
